import {
  isValidPhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input";
import type { Country } from "react-phone-number-input";

/**
 * Backend phone formats used across the app. The UI always works in E.164
 * (+9665XXXXXXXX) via the country selector; each submit converts to whatever
 * the target endpoint currently expects (see the audit) — no backend change.
 *
 *  - "e164"      → +9665XXXXXXXX   (canonical, some endpoints already expect it)
 *  - "national0" → 05XXXXXXXX      (Saudi national number with a leading 0)
 *  - "national"  → 5XXXXXXXX       (national number, no leading 0)
 *  - "cc"        → 9665XXXXXXXX    (country code without the +)
 */
export type PhoneFormat = "e164" | "national0" | "national" | "cc";

/** True when `value` is a complete, valid international phone number. */
export const isValidPhone = (value?: string | null): boolean =>
  !!value && isValidPhoneNumber(value);

/**
 * Normalise a stored phone (any of `05…`, `5…`, `9665…`, `+9665…`) into E.164
 * so it can pre-populate the country selector on edit forms. Returns undefined
 * when it can't be parsed. `defaultCountry` is assumed for national-format input.
 */
export function toE164(
  value?: string | null,
  defaultCountry: Country = "SA" as Country
): string | undefined {
  if (!value) return undefined;
  const trimmed = String(value).trim();
  try {
    const parsed = trimmed.startsWith("+")
      ? parsePhoneNumber(trimmed)
      : parsePhoneNumber(trimmed, defaultCountry);
    return parsed?.number ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Convert an E.164 phone (what the selector produces) into the format a given
 * backend endpoint expects. Falls back to the raw value if it can't be parsed.
 */
export function formatPhoneForBackend(
  value: string | undefined | null,
  format: PhoneFormat = "e164"
): string {
  if (!value) return "";
  try {
    const parsed = parsePhoneNumber(value);
    if (!parsed) return value;
    const national = parsed.nationalNumber; // e.g. "512345678"
    const cc = parsed.countryCallingCode; // e.g. "966"
    switch (format) {
      case "national0":
        return `0${national}`;
      case "national":
        return `${national}`;
      case "cc":
        return `${cc}${national}`;
      case "e164":
      default:
        return parsed.number; // +966512345678
    }
  } catch {
    return value;
  }
}
